import React from "react";
// Using the specified old package structure
import { Doughnut } from "@reactchartjs/react-chart.js";
import { Box, Typography, Card } from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import { compose } from "recompose";

const styles = (theme) => ({
  chartContainer: {
    flexGrow: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    maxWidth: "100%",
    height: 300,
    overflow: "auto",
    margin: "auto",
  },
  emptyState: {
    height: 300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
  },
});

// --- Center Text Plugin ---
const drawCenterText = (chartInstance, totalUsers) => {
  if (!chartInstance) return;
  var chart = chartInstance.chart;
  var ctx = chart.ctx;

  // Note: We no longer calculate sum from datasets here.
  // We use the totalUsers passed explicitly from the component.

  const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
  const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
  const isHovering = chart.active && chart.active.length > 0;
  ctx.save();

  // Draw "Total Participants" label
  ctx.font = "14px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#616161";
  ctx.fillText("Total Participants", centerX, centerY - 15);

  // Draw the total number
  const displayValue =
    totalUsers !== undefined && totalUsers !== null ? totalUsers : 0;

  ctx.font = "bold 36px sans-serif";
  ctx.fillStyle = isHovering ? "rgba(106, 77, 151, 0.50)" : "#673AB7"; // Purple color
  ctx.fillText(displayValue.toString(), centerX, centerY + 15);

  ctx.restore();
};

function EngagementDoughnutChart({ classes, data }) {
  // 1. Process the incoming data array
  // Default values
  let high = 0;
  let good = 0;
  let mid = 0;
  let low = 0;
  let totalUsers = 0;

  // If data exists and is an array, map the values based on engagementType
  if (Array.isArray(data) && data.length > 0) {
    data.forEach((item) => {
      const count = item.engagementPercentage || 0;
      totalUsers = data[0].totalUsers || 0;
      switch (item.engagementType) {
        case "HIGH":
          high = count;
          break;
        case "GOOD":
          good = count;
          break;
        case "MID":
          mid = count;
          break;
        case "LOW":
          low = count;
          break;
        default:
          break;
      }
    });
  }

  // 2. Handle Empty State
  // If all counts are 0, we can either show an empty chart or a message.
  // Here we check if the sum is 0 to handle purely empty responses.
  const totalCount = high + good + mid + low;
  if (!data || (Array.isArray(data) && data.length === 0)) {
    // Optional: You can return <Box className={classes.emptyState}>No Data</Box> here
    // For now, it will render an empty chart or 0s.
  }

  // 3. Construct Chart Data
  const chartData = {
    labels: [
      "High Engagement" + `(${high}%) `,
      "Good Engagement" + `(${good}%) `,
      "Mid Engagement" + `(${mid}%) `,
      "Low Engagement" + `(${low}%) `,
    ],
    datasets: [
      {
        data: [high, good, mid, low],
        backgroundColor: [
          "#4CAF50", // Green (High)
          "#3F51B5", // Blue (Good)
          "#FF9800", // Orange (Mid)
          "#F44336", // Red (Low)
        ],
        hoverBackgroundColor: ["#66BB6A", "#5C6BC0", "#FFB74D", "#E57373"],
        borderColor: "white",
        borderWidth: 2,
      },
    ],
  };

  /* ---------- EMPTY STATE ---------- */
  if (!Array.isArray(data) || data.length === 0 || totalCount === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="body1">
          No quality of engagement data available
        </Typography>
      </Box>
    );
  }
  // 4. Chart Options
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutoutPercentage: 70,
    legend: {
      display: true,
      position: "bottom",
      labels: {
        fontSize: 12,
        usePointStyle: true,
        padding: 10,
      },
    },
    hover: {
      animationDuration: 0,
    },
    animation: {
      duration: 500,
      onComplete: function () {
        drawCenterText(this, totalUsers);
      },
    },
    tooltips: {
      enabled: true,
      callbacks: {
        label: function (tooltipItem, data) {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const value = dataset.data[tooltipItem.index];
          const label = data.labels[tooltipItem.index];
          return ` ${label}`;
        },
      },
    },
  };

  return (
    <Box className={classes.chartContainer}>
      <Doughnut data={chartData} options={doughnutOptions} />
    </Box>
  );
}

export default compose(withStyles(styles, { withTheme: true }))(
  EngagementDoughnutChart
);
